/**
 * Sidebar Component
 * Left sidebar navigation with vertical icon menu
 */

import { useAuth } from '../hooks/useAuth';
import { useInvitations } from '../hooks/useInvitations';
import { useTeams } from '../hooks/useTeams';
import { useCurrentContext } from '../hooks/useCurrentContext';
import { ThemeToggle } from './ThemeToggle';
import { stringToColor } from '../utils/colors';

export function Sidebar() {
  const { user, logout } = useAuth();
  const { pendingCount } = useInvitations();
  const { teams } = useTeams();
  const context = useCurrentContext();

  const handleLogout = async () => {
    await logout();
    // Auth service handles the logout flow internally
    // Amplify will automatically show login screen when needed
  };

  if (!user) {
    return null;
  }

  // Find current team
  const currentTeam = teams.find(team => team.teamId === context.teamId);
  const teamInitial = currentTeam?.name?.[0]?.toUpperCase() || 'T';
  const teamColor = currentTeam ? stringToColor(currentTeam.name) : '4a90e2';

  return (
    <div class="w-16 bg-base-300 flex flex-col items-center py-4 border-r border-base-content/10">
      {/* Logo */}
      <a href="/teams" class="mb-4">
        <div class="w-10 h-10 rounded-lg flex items-center justify-center hover:scale-110 transition-transform">
          <img src="/favicon-32x32.png" alt="Zenmako" class="w-8 h-8" />
        </div>
      </a>

      {/* Team Selector - only show when in team context */}
      {context.scope === 'team' && teams.length > 0 && (
        <div class="mb-4 w-full flex justify-center">
          <div class="dropdown dropdown-right">
            <div
              tabIndex={0}
              role="button"
              class="w-10 h-10 rounded-lg flex items-center justify-center cursor-pointer hover:scale-110 transition-transform tooltip tooltip-right"
              data-tip="Switch Team"
              style={`background-color: #${teamColor}`}
            >
              <span class="text-white font-bold text-lg">{teamInitial}</span>
            </div>
            <ul
              tabIndex={0}
              class="menu dropdown-content z-[1] p-2 shadow bg-base-100 rounded-box w-64 mt-0 ml-2"
            >
              <li class="menu-title">
                <span>Switch Team</span>
              </li>
              {teams.map((team) => (
                <li key={team.teamId}>
                  <a
                    href={`/teams/${team.teamId}`}
                    class={context.teamId === team.teamId ? 'active' : ''}
                  >
                    <div class="flex items-center gap-3">
                      <div
                        class="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={`background-color: #${stringToColor(team.name)}`}
                      >
                        <span class="text-white font-semibold text-sm">{team.name[0].toUpperCase()}</span>
                      </div>
                      <div class="flex flex-col items-start">
                        <span class="font-semibold">{team.name}</span>
                        <span class="text-xs text-base-content/60">{team.memberCount} member{team.memberCount !== 1 ? 's' : ''}</span>
                      </div>
                    </div>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Navigation Icons */}
      <nav class="flex-1 flex flex-col gap-2 w-full items-center">
        {/* Conversations/Chat */}

        {/* Invitations */}
        {pendingCount > 0 && (
          <a
            href="/invitations"
            class="btn btn-ghost btn-sm btn-square tooltip tooltip-right indicator"
            data-tip="Invitations"
          >
            <span class="indicator-item badge badge-primary badge-xs">{pendingCount}</span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
          </a>
        )}
      </nav>

      {/* Bottom Section */}
      <div class="flex flex-col gap-2 items-center">
        {/* Theme Toggle */}
        <ThemeToggle />

        {/* User Avatar with Dropdown */}
        <div class="dropdown dropdown-right dropdown-end">
          <div tabIndex={0} role="button" class="btn btn-ghost btn-sm btn-square">
            <div class="w-8 h-8 rounded-full bg-primary text-primary-content flex items-center justify-center">
              <span class="text-sm font-semibold">
                {user.name?.[0]?.toUpperCase() || user.email[0].toUpperCase()}
              </span>
            </div>
          </div>

          <ul
            tabIndex={0}
            class="menu dropdown-content z-[1] p-2 shadow bg-base-100 rounded-box w-52 mt-2"
          >
            {/* User Info */}
            <li class="menu-title">
              <div class="flex flex-col">
                <span class="font-semibold">{user.name || 'User'}</span>
                <span class="text-xs text-base-content/60">{user.email}</span>
              </div>
            </li>

            <div class="divider my-1"></div>

            {/* Create Team */}
            <li>
              <a href="/teams?create=true">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                  />
                </svg>
                Create Team
              </a>
            </li>

            {/* Logout */}
            <li>
              <a onClick={handleLogout} class="text-error">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                  />
                </svg>
                Logout
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}
